import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Save, HelpCircle, Mail, Phone, Info, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function HelpSupportPage() {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportContact, setSupportContact] = useState('');
  const [supportText, setSupportText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isSuperadmin = user?.role === 'superadmin';

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadSupportContent();
    loadSystemSettings();
  }, []);

  const loadSystemSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSupportEmail(data.support_email || '');
        setSupportContact(data.support_contact || '');
        setSupportText(data.support_text || '');
      }
    } catch (error) {
      console.error('Failed to load system settings:', error);
    }
  };

  const loadSupportContent = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/settings/help-support`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data.content || '');
      }
    } catch (error) {
      console.error('Failed to load support content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/settings/help-support`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (response.ok) {
        toast.success('Help & Support content updated successfully');
      } else {
        toast.error('Failed to update content');
      }
    } catch (error) {
      console.error('Error saving support content:', error);
      toast.error('Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="animate-spin" size={48} style={{ color: '#652D90' }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <HelpCircle size={32} style={{ color: '#652D90' }} />
          <h1 className="text-3xl font-bold" style={{ color: '#652D90' }}>
            Help & System Support
          </h1>
        </div>

        {/* Support Contact Information Card */}
        <Card className="mb-6">
          <CardHeader style={{ backgroundColor: '#f8f9fa' }}>
            <CardTitle style={{ color: '#652D90' }}>Support Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {supportEmail || supportContact || supportText ? (
              <div className="space-y-4">
                {supportEmail && (
                  <div className="flex items-start gap-3">
                    <Mail size={20} style={{ color: '#652D90', marginTop: '2px' }} />
                    <div>
                      <p className="font-medium text-sm text-gray-600 mb-1">Support Email</p>
                      <a 
                        href={`mailto:${supportEmail}`}
                        className="text-base hover:underline"
                        style={{ color: '#652D90' }}
                      >
                        {supportEmail}
                      </a>
                    </div>
                  </div>
                )}
                
                {supportContact && (
                  <div className="flex items-start gap-3">
                    <Phone size={20} style={{ color: '#652D90', marginTop: '2px' }} />
                    <div>
                      <p className="font-medium text-sm text-gray-600 mb-1">Support Contact</p>
                      <a 
                        href={`tel:${supportContact}`}
                        className="text-base hover:underline"
                        style={{ color: '#652D90' }}
                      >
                        {supportContact}
                      </a>
                    </div>
                  </div>
                )}
                
                {supportText && (
                  <div className="flex items-start gap-3">
                    <Info size={20} style={{ color: '#652D90', marginTop: '2px' }} />
                    <div>
                      <p className="font-medium text-sm text-gray-600 mb-1">Support Information</p>
                      <p className="text-base" style={{ color: '#555555', whiteSpace: 'pre-wrap' }}>
                        {supportText}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Info size={48} className="mx-auto mb-4 opacity-20" />
                <p>No support contact information configured.</p>
                {isSuperadmin && (
                  <p className="text-sm mt-2">Configure support details in System Settings.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Help Content (Editable by Superadmin) */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Help Information</CardTitle>
          </CardHeader>
          <CardContent>
            {isSuperadmin ? (
              <div className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-sm text-blue-900">
                    As a superadmin, you can add additional help documentation here.
                    Use Markdown formatting for better readability.
                  </AlertDescription>
                </Alert>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#555555' }}>
                    Additional Help Content (Optional)
                  </label>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Enter additional help information here... (Markdown supported)"
                    rows={10}
                    className="font-mono"
                  />
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  style={{ backgroundColor: '#652D90' }}
                  className="hover:opacity-90"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2" size={16} />
                      Save Additional Content
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div>
                {content ? (
                  <div 
                    className="prose max-w-none"
                    style={{ whiteSpace: 'pre-wrap', color: '#555555', lineHeight: '1.7' }}
                  >
                    {content}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No additional help information available.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
